import { api } from './api-client';

type SuccessEnvelope<T> = { data: T };

// ── Data-Credit wallet ───────────────────────────────────────────────────────
// One active (unexpired, non-empty) pack the wallet has granted. `source` is a
// free string from the ledger (`purchase` | `auto_purchase` | `grant` | …).
export type CreditPack = {
  packId: string;
  grantedCredits: number;
  remainingCredits: number;
  source: string;
  expiresAt: string;
  createdAt: string;
};

export type CreditAutoPurchase = {
  enabled: boolean;
  monthlyCapCents: number | null;
  spendCycleCents: number;
};

export type CreditWallet = {
  balanceCredits: number;
  autoPurchase: CreditAutoPurchase;
  packs: CreditPack[];
};

// ── Usage breakdown ──────────────────────────────────────────────────────────
// Per-`reason` counts for the current billing period: Actions debited and Data
// Credits burned. `reason` is the raw ledger reason (catalog reason codes plus
// `action.*`); the UI maps known reasons to friendly labels.
export type UsageReasonRow = { reason: string; count: number };

// Current recurring Actions allowance: the active extra add-on tier (null when
// none), the resulting monthly included total, and the NET used count (debits
// minus refunds — the same figure the 402 gate enforces). Lets the add-on
// control show what's active, and lets "Actions left" (`included − used`) match
// enforcement instead of a debit-only breakdown sum.
export type ActionsAllowance = {
  tier: string | null;
  extraActions: number;
  includedActions: number;
  usedActions: number;
};

export type ActionUsage = {
  periodStart: string;
  breakdown: UsageReasonRow[];
  dataCredits: UsageReasonRow[];
  actionsAllowance: ActionsAllowance;
};

// Mutating the extra-Actions add-on returns the resolved tier (null when
// removed), the extra Actions it grants, and the recomputed included total.
export type ActionsAddonResult = {
  tier: string | null;
  extraActions: number;
  includedActions: number;
};

export const BillingCreditsService = {
  // GET /api/billing/credits (flag `actionsCredits`)
  getCredits: async (): Promise<CreditWallet> => {
    const result = await api.get<SuccessEnvelope<CreditWallet>>(
      '/billing/credits',
    );
    return result.data;
  },

  // POST /api/billing/credits/checkout (flag `actionsCredits`) — returns a
  // Stripe Checkout URL to redirect to. Stripe's `session.url` is `string |
  // null`, so the caller must guard before redirecting.
  checkoutPack: async (packId: string): Promise<string | null> => {
    const result = await api.post<SuccessEnvelope<{ url: string | null }>>(
      '/billing/credits/checkout',
      { packId },
    );
    return result.data.url;
  },

  // PUT /api/billing/credits/auto-purchase (flag `actionsCredits`)
  setAutoPurchase: async (input: {
    enabled: boolean;
    monthlyCapCents: number;
  }): Promise<{ enabled: boolean; monthlyCapCents: number }> => {
    const result = await api.put<
      SuccessEnvelope<{ enabled: boolean; monthlyCapCents: number }>
    >('/billing/credits/auto-purchase', input);
    return result.data;
  },

  // POST /api/billing/actions/addon (flag `actionsCredits`)
  setActionsAddon: async (tier: string): Promise<ActionsAddonResult> => {
    const result = await api.post<SuccessEnvelope<ActionsAddonResult>>(
      '/billing/actions/addon',
      { tier },
    );
    return result.data;
  },

  // DELETE /api/billing/actions/addon (flag `actionsCredits`)
  removeActionsAddon: async (): Promise<ActionsAddonResult> => {
    const result = await api.delete<SuccessEnvelope<ActionsAddonResult>>(
      '/billing/actions/addon',
    );
    return result.data;
  },

  // GET /api/billing/actions/usage — Actions + Data-Credit usage by reason.
  getUsage: async (): Promise<ActionUsage> => {
    const result = await api.get<SuccessEnvelope<ActionUsage>>(
      '/billing/actions/usage',
    );
    return result.data;
  },
};
